import { AsyncPipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, ElementRef, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { injectQueryClient } from '../query'
import { readLocalStorage, writeLocalStorage } from '../local-storage'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { QueryObserverResult } from '@tanstack/query-core'
import { AngularSplitModule } from 'angular-split'
import { NgxExtendedPdfViewerComponent, NgxExtendedPdfViewerModule, NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer'
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  fromEvent,
  map,
  Observable,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs'
import { citedPassagePattern } from '../meeting/cited-passage-matching'
import { firstFreshElement, onceRequestedDocumentLoads, scrollContainerTo } from './cited-passage-highlighting'
import { OpenDocumentEvent, OpenMeetingEvent } from '../meeting/tiptap-editor/extensions/citation-plugin'
import { meetingResourceTypesToUpload, MAX_AGENDA_SIZE_IN_BYTES } from '../meeting-document-upload/meeting-document-upload.component'
import { TranscriptComponent } from '../meeting/transcript/transcript.component'
import {
  Meeting,
  MeetingProcessingStatus,
  MeetingResource,
  MeetingsService,
  MetricType,
  Sentence,
  TemplateSourceType,
  computeIsCheckingAgendaValidity,
  computeAgendaUploadStatus,
  computeAgendaFailureMessageKey,
  computeShouldConfirmTemplateReplace,
  computeShouldShowTemplateSourcePicker,
  isAgendaExtractionRejectionVisible,
  canReprocessOutputs,
  isReprocessOutputsButtonVisible,
  isMinutesGenerationTargeted,
  AgendaUploadStatus,
} from '../meetings.service'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { DocumentUploadDialogComponent, DocumentUploadDialogInputData } from '../document-upload-dialog/document-upload-dialog.component'
import { LoggerService } from '../logger.service'
import { useMediaTimePrecision } from '../media-player-controller/media-player-controller.component'
import { MediaPlayerComponent } from '../media-player/media-player.component'
import { MetricsService } from '../metrics.service'
import { SpeakerXRayComponent } from '../speaker-x-ray/speaker-x-ray.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { TemplateEditorComponent } from '../template-editor.component/template-editor.component'
import { FileUploadRequest } from '../template-editor.component/template-item/template-item.component'
import { TemplateItem } from '../template-editor.component/types/template.types'
import { TemplatesService } from '../templates.service'
import { ToastService } from '../toast.service'
import { getUploadErrorMessage } from '../uploads.service'
import { UsersService } from '../users.service'
import { constructNameForUser, DocumentUploadInfo, toSourceDocumentFormat } from '../utils'
import { TemplateSourcePickerComponent } from '../template-source-picker/template-source-picker.component'
import { DocumentUploadService } from '../document-upload.service'
import {
  TemplateImportDialog,
  TemplateImportDialogInputData,
  TemplateImportDialogOutputData,
} from '../template-editor.component/template-import-dialog/template-import-dialog'
import { TemplateSaveAsDialog, TemplateSaveAsDialogOutputData } from '../template-editor.component/template-save-as-dialog/template-save-as-dialog'

const DOCUMENT_MAX_FILE_NAME_DISPLAY_LENGTH = 25
const HIGHLIGHT_RENDER_TIMEOUT_MS = 5_000
const HIGHLIGHT_SCROLL_MARGIN_PX = 50
const SHOW_TRANSCRIPT_CLOCK_TIME_KEY = 'showTranscriptClockTime'

@Component({
  selector: 'app-meeting-resources-container',
  standalone: true,
  imports: [
    AsyncPipe,
    TranslatePipe,
    TranslateDirective,
    AngularSplitModule,
    MatIcon,
    MatIconButton,
    MatTooltip,
    TranscriptComponent,
    SpeakerXRayComponent,
    SpinnerComponent,
    MediaPlayerComponent,
    NgxExtendedPdfViewerModule,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    TemplateEditorComponent,
    TemplateSourcePickerComponent,
  ],
  templateUrl: './meeting-resources-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-resources-container.component.scss',
})
export class MeetingResourcesContainerComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: true })
  meeting!: Meeting

  /** The meeting's Restricted-role users, offered per agenda item in the template editor's restricted-access picker. */
  get restrictedUsers(): { userId: number; name: string }[] {
    return this.meeting.shares
      .filter((share) => share.level === 'RESTRICTED')
      .map((share) => ({ userId: share.sharedUserId, name: constructNameForUser(share.sharedUser) }))
  }

  @Input({ required: true })
  canEdit!: boolean

  @Input({ required: true })
  userHasEditorAccess!: boolean

  @Input()
  isSensitiveModeEnabled: boolean = false

  @ViewChild(MediaPlayerComponent)
  mediaPlayer: MediaPlayerComponent | undefined = undefined

  @ViewChild('templateEditor')
  templateEditor!: TemplateEditorComponent

  @ViewChild('templateSourcePicker')
  templateSourcePicker: TemplateSourcePickerComponent | undefined

  @ViewChild('pdfViewer')
  pdfViewer: NgxExtendedPdfViewerComponent | undefined = undefined

  showTranscriptClockTime = readLocalStorage(SHOW_TRANSCRIPT_CLOCK_TIME_KEY) === 'true'

  toggleTranscriptClockTime() {
    this.showTranscriptClockTime = !this.showTranscriptClockTime
    writeLocalStorage(SHOW_TRANSCRIPT_CLOCK_TIME_KEY, this.showTranscriptClockTime.toString())
  }

  currentTab: 'MEETING' | 'DOCUMENTS' | 'AGENDA' = 'MEETING'
  documentPdfBlobs: Map<number, Blob> = new Map()
  selectedDocumentId: number | null = null
  currentPdfPage = 1

  private readonly loadedDocumentId$ = new BehaviorSubject<number | null>(null)
  private readonly citedPassageRequests$ = new Subject<{ resourceId: number; event: OpenDocumentEvent }>()
  private citedPassageSubscription: Subscription | null = null

  usersService = inject(UsersService)
  translateService = inject(TranslateService)
  currentUser$ = this.usersService.currentUser$
  isSupportUser$ = this.currentUser$.pipe(map((user) => user !== null && user.role === 'SUPPORT'))
  http = inject(HttpClient)

  router = inject(Router)
  route = inject(ActivatedRoute)
  meetingsService = inject(MeetingsService)
  toastService = inject(ToastService)
  metricsService = inject(MetricsService)
  logger = inject(LoggerService)
  dialog = inject(MatDialog)
  templatesService = inject(TemplatesService)
  documentUploadService = inject(DocumentUploadService)
  pdfViewerService = inject(NgxExtendedPdfViewerService)
  elementRef = inject<ElementRef<HTMLElement>>(ElementRef)
  queryClient = injectQueryClient()
  templateItems$!: Observable<QueryObserverResult<TemplateItem[], Error>>
  private scrollingInterval = 100
  private meetingSubscription: Subscription | null = null
  private trackTranscriptIdSubscription: Subscription | null = null
  transcriptScroll$ = new Subject<void>()
  private isAutomatedScrollingOngoing = false
  private automatedScrollSubscription: Subscription | null = null
  shouldShowSpeakers$ = this.route.queryParamMap.pipe(
    map((paramMap) => paramMap.get('showSpeakers') === 'true' || paramMap.get('speakerLabelingModeEnabled') === 'true')
  )

  speakerLabelingModeEnabled$ = this.route.queryParams.pipe(map((params) => params['speakerLabelingModeEnabled'] === 'true'))
  resetSpeakersMutation = this.meetingsService.resetSpeakersMutation()
  reprocessOutputsMutation = this.meetingsService.reprocessOutputsMutation()
  reprocessTemplateItemsMutation = this.meetingsService.reprocessTemplateItemsMutation()
  removeMeetingResourceMutation = this.meetingsService.removeResource()
  updateTemplateItemsMutation = this.templatesService.updateTemplateItems()
  loadTemplateMutation = this.templatesService.loadTemplate()
  saveAsTemplateMutation = this.templatesService.saveMeetingTemplateAsModel()
  importAgendaMutation = this.meetingsService.importAgenda()
  patchMeetingMutation = this.meetingsService.patchMutation()
  shouldNavigateTranscriptBasedOnMediaTime = true
  isTranscriptMaximized = false
  private secondsToEaseOutSentenceClicksWhenPlayingMedia = 3
  maxAgendaSize = MAX_AGENDA_SIZE_IN_BYTES
  isImportingAgenda = false

  get isExtractingAgenda(): boolean {
    return this.meeting.processingStatus === MeetingProcessingStatus.GENERATING_AGENDA_ITEMS
  }

  get agendaExtractionProgress(): number | null {
    if (!this.isExtractingAgenda) {
      return null
    }
    return this.meeting.agendaExtractionProgress
  }

  get isCheckingAgendaValidity(): boolean {
    return computeIsCheckingAgendaValidity(this.meeting)
  }

  get isAgendaExtractionFailed(): boolean {
    return isAgendaExtractionRejectionVisible(this.meeting)
  }

  get agendaUploadStatus(): AgendaUploadStatus {
    return computeAgendaUploadStatus({
      isImporting: this.isImportingAgenda,
      isFailed: this.isAgendaExtractionFailed,
      isChecking: this.isCheckingAgendaValidity,
      isUploaded: this.hasAgendaUploaded && this.meeting.templateSourceType === TemplateSourceType.UPLOAD,
    })
  }

  get isInvalidAgendaDocument(): boolean {
    return this.meeting.invalidAgendaReason !== null
  }

  get agendaFailureMessageKey(): string | null {
    return computeAgendaFailureMessageKey(this.meeting.invalidAgendaReason, this.meeting.agendaExtractionFailed)
  }

  get isCreateTemplateSelected(): boolean {
    return this.meeting.templateSourceType === TemplateSourceType.CREATE
  }

  get shouldConfirmTemplateReplace(): boolean {
    return computeShouldConfirmTemplateReplace({
      templateSourceType: this.meeting.templateSourceType,
      isCheckingAgendaValidity: this.isCheckingAgendaValidity,
      isImportingAgenda: this.isImportingAgenda,
      isAgendaExtractionFailed: this.isAgendaExtractionFailed,
      isCreateTemplateSelected: this.isCreateTemplateSelected,
      templateItemsCount: this.templateEditor?.templateItems.length ?? 0,
    })
  }

  get isPopulatingTemplate(): boolean {
    return this.isExtractingAgenda && !this.isCheckingAgendaValidity
  }

  shouldShowTemplateSourcePicker(itemCount: number): boolean {
    return computeShouldShowTemplateSourcePicker({
      isCheckingAgendaValidity: this.isCheckingAgendaValidity,
      isAgendaExtractionFailed: this.isAgendaExtractionFailed,
      hasTemplateItems: this.meeting.hasTemplateItems,
      itemCount,
      templateSourceType: this.meeting.templateSourceType,
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['meeting'] !== undefined && !changes['meeting'].firstChange) {
      this.preloadAllDocuments()
      this.validateCurrentTab()
    }
  }

  ngOnInit() {
    this.trackTranscriptScrolls()
    this.trackSentenceIdUpdates()
    this.scheduleInitialTranscriptScroll()
    this.templateItems$ = this.templatesService.getTemplateItems(this.meeting.id)
    this.preloadAllDocuments()
    this.initializeTabFromQueryParams()
    this.highlightCitedPassagesOnRequest()
  }

  private highlightCitedPassagesOnRequest() {
    this.citedPassageSubscription = onceRequestedDocumentLoads(this.citedPassageRequests$, this.loadedDocumentId$, ({ resourceId, event }) =>
      from(this.readSurroundingPageTexts(event)).pipe(
        switchMap((pageTexts) =>
          this.highlightCitedPassages(event.resourcePageNumber, citedPassagePattern(event.citedTexts, event.resourcePageNumber, pageTexts))
        ),
        catchError((error) => {
          this.logger.warn(`Could not highlight the cited passages on page ${event.resourcePageNumber} of resource ${resourceId}`, error)
          return EMPTY
        })
      )
    ).subscribe((highlight) => {
      const viewerContainer = highlight.closest<HTMLElement>('#viewerContainer')

      if (viewerContainer !== null) {
        scrollContainerTo(viewerContainer, highlight, HIGHLIGHT_SCROLL_MARGIN_PX)
      }
    })
  }

  // pdf.js's own scroll to a match uses the native `scrollIntoView`, which also scrolls every
  // container around the viewer, so the viewer is scrolled here instead, and only itself.
  private highlightCitedPassages(pageNumber: number, pattern: string | null): Observable<HTMLElement> {
    const viewerContainer = this.elementRef.nativeElement.querySelector<HTMLElement>('#viewerContainer')
    const highlightSelector = `.page[data-page-number="${pageNumber}"] .customHighlight`
    const staleHighlights = new Set(viewerContainer?.querySelectorAll(highlightSelector) ?? [])

    this.pdfViewerService.scrollPageIntoView(pageNumber)
    this.pdfViewerService.find(pattern ?? '', { highlightAll: true, regexp: pattern !== null, dontScrollIntoView: true, useSecondaryFindcontroller: true })

    if (pattern === null || viewerContainer === null) {
      return EMPTY
    }

    return firstFreshElement(viewerContainer, highlightSelector, staleHighlights, HIGHLIGHT_RENDER_TIMEOUT_MS)
  }

  private async readSurroundingPageTexts(event: OpenDocumentEvent): Promise<Map<number, string>> {
    if (event.citedTexts.length === 0) {
      return new Map()
    }

    const pageNumbers = [event.resourcePageNumber - 1, event.resourcePageNumber, event.resourcePageNumber + 1].filter(
      (pageNumber) => pageNumber >= 1 && pageNumber <= this.pdfViewerService.numberOfPages()
    )

    return new Map(await Promise.all(pageNumbers.map(async (pageNumber) => [pageNumber, await this.pdfViewerService.getPageAsText(pageNumber)] as const)))
  }

  onPdfPagesLoaded() {
    const loadedSource = this.pdfViewer?.src() ?? null
    const loadedDocumentId = [...this.documentPdfBlobs.entries()].find(([, blob]) => blob === loadedSource)?.[0] ?? null

    this.loadedDocumentId$.next(loadedDocumentId)
  }

  private initializeTabFromQueryParams() {
    const tab = this.route.snapshot.queryParamMap.get('tab')
    if (tab === 'template') {
      this.currentTab = 'AGENDA'
      this.router.navigate([], { queryParams: { tab: null }, queryParamsHandling: 'merge' })
    }
  }

  private preloadAllDocuments() {
    const resourcesWithUrls = this.meeting.userUploadedResources.filter((resource) => resource.downloadUrl !== null)
    resourcesWithUrls.forEach((resource) => {
      this.loadDocumentPdf(resource.id)
    })
  }

  private selectDefaultDocument() {
    const availableResources = this.meeting.userUploadedResources.filter((resource) => resource.downloadUrl !== null)
    const defaultDocumentId = availableResources.length > 0 ? availableResources[0].id : null

    if (defaultDocumentId !== this.selectedDocumentId) {
      this.loadedDocumentId$.next(null)
    }
    this.selectedDocumentId = defaultDocumentId
  }

  private validateCurrentTab() {
    if (this.currentTab === 'DOCUMENTS') {
      const selectedDocumentExists =
        this.selectedDocumentId !== null &&
        this.meeting.userUploadedResources.some((resource) => resource.id === this.selectedDocumentId && resource.downloadUrl !== null)
      if (selectedDocumentExists) {
        return
      }
      this.selectDefaultDocument()
      this.currentPdfPage = 1
      if (this.selectedDocumentId === null) {
        this.currentTab = 'MEETING'
      } else if (this.selectedDocumentPdfBlob === null) {
        this.loadDocumentPdf(this.selectedDocumentId)
      }
    }
  }

  get selectedDocument() {
    if (this.selectedDocumentId === null) {
      return null
    }
    return this.meeting.userUploadedResources.find((resource) => resource.id === this.selectedDocumentId) ?? null
  }

  get selectedDocumentPdfBlob(): Blob | null {
    if (this.selectedDocumentId === null) {
      return null
    }
    return this.documentPdfBlobs.get(this.selectedDocumentId) ?? null
  }

  ngOnDestroy() {
    if (this.trackTranscriptIdSubscription !== null) {
      this.trackTranscriptIdSubscription.unsubscribe()
    }
    if (this.meetingSubscription !== null) {
      this.meetingSubscription.unsubscribe()
    }
    if (this.citedPassageSubscription !== null) {
      this.citedPassageSubscription.unsubscribe()
    }
    if (this.automatedScrollSubscription !== null) {
      this.automatedScrollSubscription.unsubscribe()
    }
  }

  reprocessLlm() {
    const titleKey = this.meeting.hasMinutes ? 'MEETINGS.REPROCESS_OUTPUTS.TITLE' : 'MEETINGS.REPROCESS_OUTPUTS.TITLE_FIRST_TIME'
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(titleKey),
        description: this.translateService.instant('MEETINGS.REPROCESS_OUTPUTS.DESCRIPTION', { meetingId: this.meeting.id }),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.reprocessOutputsMutation.mutateAsync({ meetingId: this.meeting.id })
      }
    })
  }

  async reprocessTemplateItem(templateItemId: number) {
    await this.reprocessTemplateItemsMutation.mutateAsync({ meetingId: this.meeting.id, templateItemIds: [templateItemId] })
  }

  // The sentence query params can already match the citation, so the transcript is scrolled here rather than only
  // on a change of `sentenceId`.
  showCitedSentences(event: OpenMeetingEvent) {
    this.currentTab = 'MEETING'
    setTimeout(() => this.scrollToSentence(event.startSentenceId))
  }

  private trackSentenceIdUpdates() {
    this.route.queryParams
      .pipe(
        filter((params) => {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              skipScroll: null,
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          })
          return params['skipScroll'] !== 'true'
        }),
        map((params) => params['sentenceId'] ?? null),
        filter((sentenceId) => sentenceId !== null),
        distinctUntilChanged()
      )
      .subscribe((sentenceId) => {
        setTimeout(() => this.scrollToSentence(sentenceId))
      })
  }

  shouldMaximizeTranscript(meeting: Meeting) {
    return this.isTranscriptMaximized || !meeting.videoUrl
  }

  get currentProgress(): number {
    if (this.mediaPlayer === null || this.mediaPlayer === undefined) {
      return 0
    }
    const value = (this.mediaPlayer.getTime() / this.mediaPlayer.getDuration()) * 100
    if (Number.isNaN(value)) {
      return 0
    }
    return value
  }

  get activeSpeakerId(): string | null {
    if (this.mediaPlayer === null || this.mediaPlayer === undefined) {
      return null
    }

    const currentTime = this.mediaPlayer.getTime()
    const slot = this.meeting.transcript.findLast((slot) => slot.startTime < currentTime) as any // TODO The types are incorrect for transcript
    const slotSpeakerId = slot?.speaker?.id ?? null
    const speakerId = this.meeting.speakers.find((speaker) => speaker.speakerId === slotSpeakerId)?.speakerTag ?? null
    return speakerId
  }

  private scheduleInitialTranscriptScroll() {
    const sentenceId = this.route.snapshot.queryParams['sentenceId'] ?? null
    if (sentenceId === null) {
      return
    }
    setTimeout(() => this.scrollToSentence(sentenceId))
  }

  selectTimestamp(timestamp: number) {
    this.logger.info(`Selecting the media time ${timestamp}s.`)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        mediaTime: useMediaTimePrecision(timestamp),
        sentenceId: null,
        highlight: null,
        startMedia: true,
        forceMediaTimeUpdate: true,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  selectSentence(sentence: Sentence) {
    this.logger.info(`Selecting the sentence ${sentence.sentenceId} with the media time ${sentence.startTimeSeconds}s.`)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        mediaTime: Math.max(useMediaTimePrecision(sentence.startTimeSeconds) - this.secondsToEaseOutSentenceClicksWhenPlayingMedia, 0),
        sentenceId: sentence.sentenceId,
        endSentenceId: null,
        highlight: true,
        startMedia: true,
        forceMediaTimeUpdate: true,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
    this.shouldNavigateTranscriptBasedOnMediaTime = true
    this.metricsService.sendMetric(this.meeting.id, MetricType.CLICK_TRANSCRIPT_TO_REPLAY_VIDEO)
  }

  async resetSpeakers(meeting: Meeting): Promise<void> {
    await this.resetSpeakersMutation.mutateAsync({ meetingId: meeting.id })
    this.toastService.show('MEETINGS.RESET_SPEAKERS_SUCCESS')
  }

  private trackTranscriptScrolls() {
    this.transcriptScroll$.pipe(debounceTime(this.scrollingInterval)).subscribe(() => {
      if (this.isAutomatedScrollingOngoing) {
        return
      }
      this.logger.info('Disabling the media time tracking for transcript due to a scroll by user.')
      this.shouldNavigateTranscriptBasedOnMediaTime = false
      const sentences = document.querySelectorAll('[data-sentence-id]')
      const sentence =
        Array.from(sentences).find((sentence: any) => {
          const scrollingContainer = document.querySelector('.scrolling-container')!
          return sentence.offsetTop >= scrollingContainer.scrollTop
        }) ?? null
      if (sentence === null) {
        return
      }
      const sentenceId = sentence.getAttribute('data-sentence-id')
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          sentenceId,
          endSentenceId: null,
          highlight: null,
          skipScroll: true,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
    })
  }

  scrollToSentence(sentenceId: string) {
    const element = document.querySelector(`.sentence[data-sentence-id="${sentenceId}"]`) ?? null
    if (element === null) {
      this.logger.error(`Skipping scrolling due to not finding the sentence ${sentenceId}.`)
      return
    }
    if (this.automatedScrollSubscription !== null) {
      this.logger.info(`Redirecting the ongoing automated scroll to the sentence ${sentenceId}.`)
      this.automatedScrollSubscription.unsubscribe()
    }
    this.logger.info('Enabling the scrolling lock due to there being an automated scroll ongoing.')
    this.isAutomatedScrollingOngoing = true

    this.logger.info(`The active sentence (${sentenceId}) is not on the screen. Scrolling to the sentence.`, element)
    element.scrollIntoView({ block: 'start', behavior: 'smooth' })
    this.automatedScrollSubscription = this.onScrollComplete()
      .pipe(delay(this.scrollingInterval))
      .subscribe(() => {
        this.logger.info('Releasing the scrolling lock as the automated scroll has completed.')
        this.isAutomatedScrollingOngoing = false
        this.automatedScrollSubscription = null
      })
  }

  private onScrollComplete() {
    return fromEvent(document.querySelector('.scrolling-container')!, 'scroll').pipe(startWith(null), debounceTime(this.scrollingInterval), take(1))
  }

  async handleDocumentsTabClick() {
    if (this.meeting.userUploadedResources.length > 0) {
      this.currentTab = 'DOCUMENTS'
      if (this.selectedDocumentId === null) {
        this.selectDefaultDocument()
      }
      if (this.selectedDocumentId !== null && this.selectedDocumentPdfBlob === null) {
        await this.loadDocumentPdf(this.selectedDocumentId)
      }
      return
    }
    if (!this.isAuthorizedToManageDocuments) {
      return
    }
    const dialogRef = this.dialog.open(DocumentUploadDialogComponent, {
      width: '400px',
      data: {
        meetingId: this.meeting.id,
        title: this.translateService.instant('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.AGENDA'),
        documentToUpload: meetingResourceTypesToUpload['AGENDA'],
        type: 'agenda',
        afterUpload: async (resource: MeetingResource) => {
          return this.meeting.userUploadedResources.find(({ id }) => id === resource.id) !== undefined
        },
      } as DocumentUploadDialogInputData,
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result !== null && result !== undefined) {
        await this.onDocumentSelectionChange(result.id)
      }
    })
  }

  handleAgendaTabClick() {
    this.currentTab = 'AGENDA'
  }

  jumpToTranscript() {
    this.shouldNavigateTranscriptBasedOnMediaTime = true
    this.mediaPlayer?.updateTranscriptToMatchMediaTime()
  }

  get displayMeeting() {
    return this.currentTab === 'MEETING'
  }

  get displayDocuments() {
    return this.currentTab === 'DOCUMENTS'
  }

  get displayAgenda() {
    return this.currentTab === 'AGENDA'
  }

  get isAuthorizedToManageDocuments() {
    return this.meeting.accessLevel === 'FULL_ACCESS' || this.meeting.accessLevel === 'EDITOR'
  }

  async removeAgenda() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.REMOVE_AGENDA_TITLE'),
        description: this.translateService.instant('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.REMOVE_AGENDA_DESCRIPTION'),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const agenda = this.meeting.userUploadedResources.find((resource) => resource.resourceType === 'AGENDA') ?? null
        if (agenda === null) {
          return
        }
        await this.removeMeetingResourceMutation.mutateAsync({
          meetingId: this.meeting.id,
          resourceType: agenda.resourceType,
          index: agenda.index,
          extension: agenda.extension,
        })
        this.toastService.show('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.AGENDA_REMOVED_SUCCESS')
        this.currentTab = 'MEETING'
      }
    })
  }

  private async loadDocumentPdf(resourceId: number): Promise<void> {
    const resource = this.meeting.userUploadedResources.find((resource) => resource.id === resourceId) ?? null
    if (resource === null || resource.downloadUrl === null || this.documentPdfBlobs.has(resourceId)) {
      return
    }

    try {
      const blob = (await firstValueFrom(this.http.get(resource.downloadUrl, { responseType: 'blob' }))) ?? null
      if (blob !== null) {
        this.documentPdfBlobs.set(resourceId, blob)
        this.logger.info(`Document PDF blob loaded successfully for resource ${resourceId}`)
      }
    } catch (error) {
      this.logger.error(`Failed to load document PDF for resource ${resourceId}`, error)
    }
  }

  async onDocumentSelectionChange(resourceId: number) {
    if (this.selectedDocumentId !== resourceId) {
      this.loadedDocumentId$.next(null)
    }
    this.selectedDocumentId = resourceId
    this.currentPdfPage = 1
    this.currentTab = 'DOCUMENTS'
    if (!this.documentPdfBlobs.has(resourceId)) {
      await this.loadDocumentPdf(resourceId)
    }
  }

  get allowReprocessingOutputs() {
    return canReprocessOutputs(this.meeting)
  }

  get isReprocessOutputsButtonVisible() {
    return isReprocessOutputsButtonVisible(this.meeting)
  }

  // A targeted regeneration only locks the items it covers, not the whole editor.
  get showTemplateEditorProcessingStatus(): boolean {
    return !this.meeting.isPostProcessingComplete && !isMinutesGenerationTargeted(this.meeting)
  }

  async saveTemplate(items: TemplateItem[]) {
    try {
      const response = await this.updateTemplateItemsMutation.mutateAsync({ meetingId: this.meeting.id, items })
      // Apply the id mapping before confirming success: confirmSaveSuccess can synchronously trigger a
      // queued save (pendingSaveAfterCurrent), which builds its payload from _templateItems immediately -
      // if that still has temp ids for what this save just created, the backend treats them as new
      // creates instead of updates, producing duplicates.
      this.templateEditor.applyIdMapping(response.clientIdToServerIdMapping)
      this.templateEditor.confirmSaveSuccess()
    } catch (error) {
      this.logger.error('Failed to save template:', error)
      this.templateEditor.confirmSaveFailure()
    }
  }

  async handleFileUpload(request: FileUploadRequest, meetingId: number) {
    await this.documentUploadService.uploadCustomDocumentSourceToBlock(meetingId, request.blockId, request.file, this.templateEditor)
  }

  async handleOpenDocumentEvent(event: OpenDocumentEvent) {
    const resource = this.meeting.userUploadedResources.find((resource) => resource.id === event.resourceId)

    if (resource === undefined) {
      console.warn(`Could not find user uploaded resource with ID ${event.resourceId} to switch to.`)
      return
    }

    if (resource.downloadUrl === null) {
      console.warn(`Could not switch to user uploaded resource with ID ${event.resourceId} as it has no download URL.`)
      return
    }

    if (this.selectedDocumentId !== resource.id || this.currentTab !== 'DOCUMENTS') {
      await this.onDocumentSelectionChange(resource.id)
    }

    this.currentPdfPage = event.resourcePageNumber
    this.citedPassageRequests$.next({ resourceId: resource.id, event })
  }

  get papersTabLabel(): string {
    const papersLabel = this.translateService.instant('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.PAPERS')
    if (this.selectedDocument === null) {
      return papersLabel
    }
    const fileNameWithoutExtension = this.selectedDocument.fileName.replace(/\.[^/.]+$/, '')
    const truncatedFileName =
      fileNameWithoutExtension.length > DOCUMENT_MAX_FILE_NAME_DISPLAY_LENGTH
        ? fileNameWithoutExtension.substring(0, DOCUMENT_MAX_FILE_NAME_DISPLAY_LENGTH) + '...'
        : fileNameWithoutExtension
    return `${papersLabel} (${truncatedFileName})`
  }

  get hasUnsavedTemplateChanges(): boolean {
    return this.templateEditor?.hasUnsavedChanges ?? false
  }

  get hasAgendaUploaded(): boolean {
    return this.meeting.userUploadedResources.some((resource) => resource.resourceType === 'AGENDA')
  }

  get uploadedAgendaFileName(): string | null {
    const agendaResource = this.meeting.userUploadedResources.find((resource) => resource.resourceType === 'AGENDA')
    return agendaResource?.fileName ?? null
  }

  async onUploadConfirmationRequested() {
    const confirmed = await this.showTemplateReplaceConfirmation()
    if (confirmed && this.templateSourcePicker !== undefined) {
      this.templateSourcePicker.openFileDialog()
    }
  }

  private async showTemplateReplaceConfirmation(): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.TITLE'),
        description: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.DESCRIPTION'),
        confirmLabel: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.CONFIRM'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })
    return await firstValueFrom(dialogRef.afterClosed())
  }

  async onAgendaFileChange(documentUploadInfo: DocumentUploadInfo | null): Promise<void> {
    if (documentUploadInfo === null) {
      return
    }

    this.isImportingAgenda = true
    try {
      await this.importAgendaMutation.mutateAsync({
        meetingId: this.meeting.id,
        data: {
          targetResourceExtension: documentUploadInfo.extension,
          uploadKey: documentUploadInfo.key,
          fileName: documentUploadInfo.fileName,
          sourceDocumentFormat: toSourceDocumentFormat(documentUploadInfo.extension),
        },
      })
      this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.AGENDA_UPLOADED')
    } catch (error) {
      this.logger.error('Failed to upload agenda:', error)
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage)
      } else {
        this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.AGENDA_UPLOAD_FAILED')
      }
    } finally {
      this.isImportingAgenda = false
    }
  }

  onLoadSavedTemplate() {
    this.templatesService
      .getTemplateModels()
      .pipe(
        filter((result) => result.isSuccess),
        take(1)
      )
      .subscribe((result) => {
        const dialogData: TemplateImportDialogInputData = {
          templates: result.data,
        }
        const dialogRef = this.dialog.open(TemplateImportDialog, {
          width: '500px',
          data: dialogData,
        })

        dialogRef.afterClosed().subscribe((dialogResult: TemplateImportDialogOutputData | null) => {
          if (dialogResult !== null && dialogResult !== undefined) {
            this.loadTemplateFromModel(dialogResult.templateId)
          }
        })
      })
  }

  private async loadTemplateFromModel(templateId: number): Promise<void> {
    this.templateEditor.clearUnsavedChanges()
    await this.loadTemplateMutation.mutateAsync({
      meetingId: this.meeting.id,
      templateId,
    })
  }

  async createBlankTemplate(): Promise<void> {
    await this.updateTemplateItemsMutation.mutateAsync({
      meetingId: this.meeting.id,
      items: [],
    })
    this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.BLANK_TEMPLATE_CREATED')
  }

  onSaveAsTemplate(items: TemplateItem[]) {
    const dialogRef = this.dialog.open(TemplateSaveAsDialog, {
      width: '400px',
    })

    dialogRef.afterClosed().subscribe(async (result: TemplateSaveAsDialogOutputData | null) => {
      if (result !== null) {
        await this.saveAsTemplateMutation.mutateAsync({
          clientId: this.meeting.clientId,
          name: result.name,
          items,
        })
      }
    })
  }
}
